import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PortalLayout from './PortalLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Plus, Search, FileText, Send, CheckCircle, XCircle, Clock,
  MoreVertical, Eye, RefreshCw, Copy, Scale, ChevronLeft, ChevronRight,
  MinusCircle, PlusCircle, AlertTriangle, Edit, Ban
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const AdjustmentListPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [adjustments, setAdjustments] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const limit = 20;

  const token = localStorage.getItem('einvoice_token');
  const headers = { 'Authorization': `Bearer ${token}` };

  const fmt = (v) => new Intl.NumberFormat('en-MY', { style: 'currency', currency: 'MYR' }).format(v || 0);

  const fetchAdjustments = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      if (search) params.set('q', search);
      if (filterType !== 'all') params.set('adjustment_type', filterType);
      if (filterStatus !== 'all') params.set('status', filterStatus);

      const res = await fetch(`${API}/api/einvoice/gateway/adjustments?${params}`, { headers });
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      setAdjustments(data.adjustments || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error('Failed to load adjustments');
    } finally {
      setLoading(false);
    }
  }, [page, search, filterType, filterStatus]);

  useEffect(() => { fetchAdjustments(); }, [fetchAdjustments]);

  const getTypeBadge = (type) => {
    if (type === 'credit_note') return <Badge className="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 gap-1"><MinusCircle className="w-3 h-3" />CN</Badge>;
    return <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 gap-1"><PlusCircle className="w-3 h-3" />DN</Badge>;
  };

  const getStatusBadge = (status) => {
    const map = {
      draft: { icon: FileText, cls: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300', label: 'DRAFT' },
      submitted: { icon: Send, cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', label: 'SUBMITTED' },
      valid: { icon: CheckCircle, cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400', label: 'VALID' },
      rejected: { icon: XCircle, cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', label: 'REJECTED' },
      error: { icon: AlertTriangle, cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', label: 'ERROR' },
      cancelled: { icon: Ban, cls: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400', label: 'CANCELLED' },
    };
    const c = map[status] || map.draft;
    const Icon = c.icon;
    return <Badge className={`flex items-center gap-1 text-xs ${c.cls}`}><Icon className="w-3 h-3" />{c.label}</Badge>;
  };

  const totalPages = Math.ceil(total / limit);

  const handleSubmitToLhdn = async (adj) => {
    if (!window.confirm(`Submit ${adj.adjustment_number} to LHDN?`)) return;
    try {
      const res = await fetch(`${API}/api/einvoice/gateway/adjustments/${adj.id}/submit-lhdn`, {
        method: 'POST', headers
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Submission failed');
      toast.success(`${adj.adjustment_number} submitted to LHDN`);
      fetchAdjustments();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleClone = async (adj) => {
    try {
      const res = await fetch(`${API}/api/einvoice/gateway/adjustments/${adj.id}/clone`, {
        method: 'POST', headers
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Clone failed');
      toast.success(`Cloned as ${data.adjustment_number}`);
      fetchAdjustments();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <PortalLayout>
      <div className="p-4 md:p-6 space-y-6" data-testid="adjustment-list-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 dark:text-gray-100 flex items-center gap-2">
              <Scale className="w-6 h-6 text-purple-600" />
              Adjustment (CN/DN)
            </h1>
            <p className="text-sm text-slate-500 dark:text-gray-400 mt-1">
              Credit Notes & Debit Notes for LHDN-submitted invoices
            </p>
          </div>
          <Button
            className="bg-purple-600 hover:bg-purple-700"
            onClick={() => navigate('/einvoice/portal/adjustment/create')}
            data-testid="create-adjustment-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Create Adjustment
          </Button>
        </div>

        {/* Warning Banner */}
        <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 flex items-start gap-2" data-testid="adjustment-banner">
          <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
          <p className="text-xs text-amber-700 dark:text-amber-300">
            This document is an Adjustment (CN/DN) submitted to LHDN. It does <strong>NOT</strong> modify the original invoice.
            For booking changes or corrections before submission, please update in your PMS system.
          </p>
        </div>

        {/* Filters */}
        <Card className="dark:bg-[#1a1a2e]">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-3 items-center">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search adjustment / invoice / customer..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  className="pl-10"
                  data-testid="adjustment-search"
                />
              </div>
              <Select value={filterType} onValueChange={(v) => { setFilterType(v); setPage(1); }}>
                <SelectTrigger className="w-[140px]" data-testid="filter-type">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="credit_note">Credit Note</SelectItem>
                  <SelectItem value="debit_note">Debit Note</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={(v) => { setFilterStatus(v); setPage(1); }}>
                <SelectTrigger className="w-[140px]" data-testid="filter-status">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="submitted">Submitted</SelectItem>
                  <SelectItem value="valid">Valid</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="icon" onClick={fetchAdjustments} data-testid="refresh-btn">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="dark:bg-[#1a1a2e]">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Adjustment No</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Original Invoice</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>LHDN UUID</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-12">
                      <RefreshCw className="w-5 h-5 animate-spin mx-auto text-slate-400" />
                    </TableCell>
                  </TableRow>
                ) : adjustments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-12" data-testid="empty-state">
                      <Scale className="w-10 h-10 text-slate-300 dark:text-gray-600 mx-auto mb-3" />
                      <p className="text-slate-500 dark:text-gray-400 font-medium">No adjustments available.</p>
                      <p className="text-xs text-slate-400 dark:text-gray-500 mt-1">
                        Adjustments can only be made after invoice submission and LHDN validation.
                      </p>
                    </TableCell>
                  </TableRow>
                ) : adjustments.map((adj) => (
                  <TableRow key={adj.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/30 cursor-pointer"
                    onClick={() => navigate(`/einvoice/portal/adjustment/${adj.id}`)}>
                    <TableCell className="font-mono text-sm font-medium" data-testid={`adj-number-${adj.id}`}>
                      {adj.adjustment_number}
                    </TableCell>
                    <TableCell>{getTypeBadge(adj.adjustment_type)}</TableCell>
                    <TableCell className="font-mono text-xs text-slate-500 dark:text-gray-400">
                      {adj.original_invoice_number}
                    </TableCell>
                    <TableCell className="text-sm">{adj.guest_name}</TableCell>
                    <TableCell className={`text-right font-mono font-medium ${adj.adjustment_type === 'credit_note' ? 'text-red-600' : 'text-green-600'}`}>
                      {adj.adjustment_type === 'credit_note' ? '-' : '+'}{fmt(adj.amount)}
                    </TableCell>
                    <TableCell>{getStatusBadge(adj.status)}</TableCell>
                    <TableCell className="font-mono text-xs text-slate-400 max-w-[120px] truncate">
                      {adj.lhdn_uuid || '-'}
                    </TableCell>
                    <TableCell className="text-xs text-slate-500 dark:text-gray-400">
                      {adj.created_at ? new Date(adj.created_at).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`adj-actions-${adj.id}`}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/einvoice/portal/adjustment/${adj.id}`)}>
                            <Eye className="w-4 h-4 mr-2" /> View Details
                          </DropdownMenuItem>
                          {adj.status === 'draft' && (
                            <DropdownMenuItem onClick={() => navigate(`/einvoice/portal/adjustment/create?edit=${adj.id}`)}>
                              <Edit className="w-4 h-4 mr-2" /> Edit
                            </DropdownMenuItem>
                          )}
                          {adj.status === 'draft' && (
                            <DropdownMenuItem onClick={() => handleSubmitToLhdn(adj)}>
                              <Send className="w-4 h-4 mr-2" /> Submit to LHDN
                            </DropdownMenuItem>
                          )}
                          {adj.status === 'rejected' && (
                            <>
                              <DropdownMenuItem onClick={() => navigate(`/einvoice/portal/adjustment/create?edit=${adj.id}`)}>
                                <Edit className="w-4 h-4 mr-2" /> Fix & Resubmit
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleClone(adj)}>
                                <Copy className="w-4 h-4 mr-2" /> Clone Adjustment
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Pagination */}
        <div className="flex items-center justify-between" data-testid="adjustment-pagination">
          <p className="text-xs text-slate-500 dark:text-gray-400">
            {total > 0 ? `Showing ${(page - 1) * limit + 1}–${Math.min(page * limit, total)} of ${total} adjustments` : 'No records'}
          </p>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page <= 1}
              onClick={() => setPage(p => p - 1)} data-testid="adj-page-prev">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            {totalPages > 0 && [...Array(Math.min(totalPages, 5))].map((_, i) => {
              const p = page <= 3 ? i + 1 : page - 2 + i;
              if (p < 1 || p > totalPages) return null;
              return (
                <Button key={p} variant={p === page ? "default" : "outline"} size="sm"
                  className={`h-7 w-7 p-0 text-xs ${p === page ? 'bg-blue-600 text-white' : ''}`}
                  onClick={() => setPage(p)} data-testid={`adj-page-${p}`}>
                  {p}
                </Button>
              );
            })}
            <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)} data-testid="adj-page-next">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </PortalLayout>
  );
};

export default AdjustmentListPage;
